import { createPortal } from 'react-dom'
import { GlobalStyles } from '@mui/material'
import { LARGEUR_TICKET_MM, TOLERANCE_PAGE_MM } from './TicketCaisse.js'

/**
 * Mécanique d'impression des documents de vente, en un seul endroit.
 *
 * ⭐ La fiche de vente et la CAISSE impriment le même ticket, et les règles qui
 * le font sortir juste sont trop subtiles pour être recopiées : largeur de page
 * imposée par le pilote, hauteur mesurée, marge de coupe, sortie de l'arbre de
 * l'application. Deux copies auraient divergé à la première retouche — et le
 * défaut ne se serait vu que sur le papier, chez le commerçant.
 */

/** Ce que la fenêtre d'impression doit produire. */
export type FormatImpression = 'facture' | 'ticket'

/**
 * Le bloc que la règle d'impression laisse seul visible.
 *
 * ⚠️ Il vit à la racine du CORPS DE PAGE, pas dans l'application. La règle
 * masquait l'application par `visibility: hidden` — or un bloc invisible reste
 * MIS EN PAGE : la hauteur de l'écran continuait de compter et le navigateur
 * annonçait « 2 feuilles » pour un ticket de dix lignes, la seconde vierge.
 */
const RACINE = 'impression-racine'

/**
 * Hauteur de page du ticket, mesurée sur le bloc réellement rendu.
 *
 * ⚠️ `size: 80mm auto` NE SUFFIT PAS sur une thermique. « auto » laisse la
 * hauteur au pilote, et le pilote d'un rouleau ne propose que des pages FIXES —
 * 210 mm, 297 mm, 3276 mm sur la POS-90. La sienne par défaut valant 297 mm,
 * chaque ticket déroulait la hauteur d'une A4 pour trois lignes d'articles.
 *
 * ⚠️ À appeler APRÈS avoir rendu le document, et de façon synchrone : voir
 * `flushSync` chez les appelants.
 */
export function mesurerPageTicket(): string {
  const bloc = document.getElementById('ticket')
  if (!bloc) return `${LARGEUR_TICKET_MM}mm auto`
  // ⚠️ Le contenu comprend DÉJÀ le bloc blanc de coupe : il fait partie du
  // ticket, pas de la page. On n'ajoute ici qu'un battement de mesure.
  const contenu = Math.ceil((bloc.getBoundingClientRect().height / 96) * 25.4)
  // Bornes du pilote pour un papier défini par l'utilisateur : hors de là, la
  // règle serait écartée en bloc et l'on retomberait sur ses 297 mm.
  const hauteur = Math.min(Math.max(contenu + TOLERANCE_PAGE_MM, 40), 400)
  return `${LARGEUR_TICKET_MM}mm ${hauteur}mm`
}

/**
 * Règles de page. On masque l'application et on ne laisse que le document : le
 * navigateur sait déjà imprimer, et sait aussi enregistrer en PDF.
 */
export function ReglesImpression({
  format,
  pageTicket,
}: {
  format: FormatImpression
  /** Mesuré juste avant d'imprimer ; `null` tant qu'on n'a pas imprimé. */
  pageTicket: string | null
}): JSX.Element {
  return (
    <GlobalStyles
      styles={{
        '@media print': {
          // ⚠️ `display: none`, pas `visibility: hidden` : seul le premier
          // retire le bloc de la mise en page.
          'body > *': { display: 'none' },
          [`#${RACINE}`]: { display: 'block' },
          // `@page` porte déjà les marges : les 8 px du corps décalaient le
          // ticket sur un rouleau où chaque millimètre de largeur est compté.
          body: { margin: 0 },
          '#ticket': {
            // Le document reprend le fil normal : déporté hors écran, il ne
            // sert qu'à rester invisible tant qu'on n'imprime pas.
            position: 'static',
            left: 'auto',
            top: 'auto',
            /**
             * ⚠️ On NE TOUCHE PAS au retrait du ticket : il porte le sien
             * (`MARGE_INTERNE_MM`), à l'intérieur de ses 72 mm. La règle
             * imposait autrefois `1rem` à tous les documents, ce qui portait
             * la page du ticket à 80,5 mm — au-delà de ce que la tête sait
             * couvrir — et le navigateur réduisait tout pour le faire entrer.
             * `undefined` laisse la propriété de côté plutôt que de la forcer.
             */
            padding: format === 'ticket' ? undefined : '1rem',
          },
        },
        '@page':
          format === 'ticket'
            ? { size: pageTicket ?? `${LARGEUR_TICKET_MM}mm auto`, margin: 0 }
            : { margin: '10mm' },
      }}
    />
  )
}

/** Sort le document de l'arbre de l'application — voir `RACINE`. */
export function PortailImpression({ children }: { children: React.ReactNode }): JSX.Element {
  return createPortal(<div id={RACINE}>{children}</div>, document.body)
}
